import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getDateString } from "@/lib/family-date";
import { getISOWeek, startOfISOWeek, addDays, format, parseISO, differenceInCalendarDays, getDay } from "date-fns";
import { getRepeatTimeForDay, getRepeatGraceMinutes } from "./useReminders";
import type { ChildWithFamily } from "./useAllChildren";

export interface ChildDashboardStatus {
  childId: string;
  taskTotal: number;
  taskDone: number;
  taskProgress: number; // 0–100
  /** Active (incomplete) homework count */
  homeworkCount: number;
  /** Active (undismissed) reminder count */
  reminderCount: number;
  /** Urgent alerts to display */
  alerts: DashboardAlert[];
}

export interface DashboardAlert {
  type: "overdue_homework" | "due_today_homework" | "unseen_reminder" | "overdue_reminder";
  label: string;
  count?: number;
}

/** Keywords indicating a submission/delivery deadline → always Friday */
const SUBMISSION_KEYWORDS = ["teams", "lever", "send inn", "innlevering", "last opp"];
const PREP_KEYWORDS = ["pakkeliste", "forbered", "ta med", "husk å ta med", "gjør klar", "klargjør", "finn frem"];

function computeEffectiveDeadline(hw: { title: string; due_date: string | null; scope: string; week_number: number; original_text: string | null; subtask_titles: string[] }): string | null {
  if (hw.due_date) return hw.due_date;
  if (hw.scope === "week_due" || hw.scope === "flexible_week") {
    const year = new Date().getFullYear();
    const jan4 = new Date(year, 0, 4);
    const startOfWeek1 = startOfISOWeek(jan4);
    const monday = addDays(startOfWeek1, (hw.week_number - 1) * 7);

    // Check if it's a prep task (Sunday) vs submission task (Friday)
    const allText = [hw.title, hw.original_text || "", ...hw.subtask_titles].join(" ").toLowerCase();
    if (SUBMISSION_KEYWORDS.some((kw) => allText.includes(kw))) {
      return format(addDays(monday, 4), "yyyy-MM-dd"); // Friday
    }
    if (hw.scope === "flexible_week") {
      return format(addDays(monday, 6), "yyyy-MM-dd"); // Sunday
    }
    const titleText = (hw.title + " " + (hw.original_text || "")).toLowerCase();
    const isPrep = PREP_KEYWORDS.some((kw) => titleText.includes(kw));
    return format(addDays(monday, isPrep ? 6 : 4), "yyyy-MM-dd");
  }
  return null;
}

/**
 * Fetches today's task item counts (total + done) for a list of children,
 * plus urgent homework and unseen reminders for dashboard alerts.
 */
export function useParentDashboardStatus(children: ChildWithFamily[], timezone: string) {
  const childIds = children.map((c) => c.id);
  const today = getDateString(new Date(), timezone);
  const currentWeek = getISOWeek(new Date());
  const queryClient = useQueryClient();
  const childIdsKey = childIds.join(",");

  const queryKey = ["parent", "dashboard-status", childIdsKey, today];

  const result = useQuery({
    queryKey,
    queryFn: async () => {
      if (childIds.length === 0) return new Map<string, ChildDashboardStatus>();

      // Fetch tasks, homework, unseen reminders (one-off + recurring occurrences) in parallel
      const [tasksRes, homeworkRes, remindersRes, allRemindersRes, unseenOccurrencesRes] = await Promise.all([
        supabase
          .from("task_items" as any)
          .select("child_id, status")
          .in("child_id", childIds)
          .eq("date", today),
        supabase
          .from("homework" as any)
          .select("child_id, title, due_date, scope, week_number, original_text, completed_at, homework_subtasks(title)")
          .in("child_id", childIds)
          .is("completed_at", null)
          .gte("week_number", currentWeek - 1)
          .lte("week_number", currentWeek),
        // One-off reminders with remind_date that are unseen
        supabase
          .from("reminders")
          .select("child_id, remind_date, seen_by_child_at, category")
          .in("child_id", childIds)
          .is("dismissed_at", null)
          .is("seen_by_child_at", null)
          .not("remind_date", "is", null)
          .lte("remind_date", today),
        supabase
          .from("reminders")
          .select("child_id")
          .in("child_id", childIds)
          .is("dismissed_at", null),
        // Recurring reminder occurrences for today that are unseen
        supabase
          .from("reminder_occurrences")
          .select("reminder_id, reminders!inner(child_id, repeat_rule)")
          .eq("occurrence_date", today)
          .is("seen_by_child_at", null)
          .is("confirmed_at", null)
          .in("reminders.child_id", childIds),
      ]);

      if (tasksRes.error) throw tasksRes.error;

      const map = new Map<string, ChildDashboardStatus>();

      // Initialize all children
      for (const id of childIds) {
        map.set(id, { childId: id, taskTotal: 0, taskDone: 0, taskProgress: 0, homeworkCount: 0, reminderCount: 0, alerts: [] });
      }

      // Count tasks
      for (const row of (tasksRes.data as any[]) || []) {
        const entry = map.get(row.child_id);
        if (!entry) continue;
        entry.taskTotal++;
        if (row.status === "done") entry.taskDone++;
      }

      // Process homework alerts + counts
      for (const hw of (homeworkRes.data as any[]) || []) {
        const entry = map.get(hw.child_id);
        if (!entry) continue;
        entry.homeworkCount++;
        const subtaskTitles = (hw.homework_subtasks || []).map((st: any) => st.title);
        const deadline = computeEffectiveDeadline({
          title: hw.title,
          due_date: hw.due_date,
          scope: hw.scope,
          week_number: hw.week_number,
          original_text: hw.original_text,
          subtask_titles: subtaskTitles,
        });
        if (!deadline) continue;
        const diff = differenceInCalendarDays(parseISO(deadline), parseISO(today));
        if (diff < 0) {
          // Overdue
          const existing = entry.alerts.find((a) => a.type === "overdue_homework");
          if (existing && existing.count) {
            existing.count++;
          } else {
            entry.alerts.push({ type: "overdue_homework", label: "Forfalte lekser", count: 1 });
          }
        } else if (diff === 0) {
          const existing = entry.alerts.find((a) => a.type === "due_today_homework");
          if (existing && existing.count) {
            existing.count++;
          } else {
            entry.alerts.push({ type: "due_today_homework", label: "Leksefrist i dag", count: 1 });
          }
        }
      }

      // Count total active reminders per child
      for (const r of (allRemindersRes.data as any[]) || []) {
        const entry = map.get(r.child_id);
        if (!entry) continue;
        entry.reminderCount++;
      }

      // Process unseen one-off reminder alerts (due today or earlier)
      for (const r of (remindersRes.data as any[]) || []) {
        const entry = map.get(r.child_id);
        if (!entry) continue;
        const isOverdue = r.remind_date && r.remind_date < today;
        const alertType: DashboardAlert["type"] = isOverdue ? "overdue_reminder" : "unseen_reminder";
        const alertLabel = isOverdue ? "Forfalte påminnelser" : "Uleste påminnelser";
        const existing = entry.alerts.find((a) => a.type === alertType);
        if (existing && existing.count) {
          existing.count++;
        } else {
          entry.alerts.push({ type: alertType, label: alertLabel, count: 1 });
        }
      }

      // Process unseen recurring reminder occurrences for today
      const now = new Date();
      const jsDay = getDay(now); // 0=Sun
      const isoDay = jsDay === 0 ? 7 : jsDay;

      for (const occ of (unseenOccurrencesRes.data as any[]) || []) {
        const childId = occ.reminders?.child_id;
        const repeatRule = occ.reminders?.repeat_rule;
        if (!childId) continue;
        const entry = map.get(childId);
        if (!entry) continue;

        let isOverdue = false;
        const scheduledTime = getRepeatTimeForDay(repeatRule, isoDay);
        if (scheduledTime) {
          const grace = getRepeatGraceMinutes(repeatRule);
          const [h, m] = scheduledTime.split(":").map(Number);
          const deadline = new Date();
          deadline.setHours(h, m + grace, 0, 0);
          isOverdue = now > deadline;
        }

        const alertType: DashboardAlert["type"] = isOverdue ? "overdue_reminder" : "unseen_reminder";
        const alertLabel = isOverdue ? "Forfalte påminnelser" : "Uleste påminnelser";
        const existing = entry.alerts.find((a) => a.type === alertType);
        if (existing && existing.count) {
          existing.count++;
        } else {
          entry.alerts.push({ type: alertType, label: alertLabel, count: 1 });
        }
      }

      // Compute progress
      for (const entry of map.values()) {
        entry.taskProgress = entry.taskTotal > 0
          ? Math.round((entry.taskDone / entry.taskTotal) * 100)
          : 0;
      }

      return map;
    },
    enabled: childIds.length > 0,
  });

  // Realtime listener: invalidate dashboard query on relevant table changes
  useEffect(() => {
    if (childIds.length === 0) return;

    const channel = supabase
      .channel(`parent-dashboard-${childIdsKey}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "task_items" }, () => {
        queryClient.invalidateQueries({ queryKey });
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "reminders" }, () => {
        queryClient.invalidateQueries({ queryKey });
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "reminder_occurrences" }, () => {
        queryClient.invalidateQueries({ queryKey });
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "homework_subtasks" }, () => {
        queryClient.invalidateQueries({ queryKey });
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [childIdsKey, queryClient]);

  return result;
}
